import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POSITIONS = ["top", "left", "right"];
const empty = { position: "top", title: "", url: "", image: [] };

export default function BannerTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/banners").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setForm(item ? { position: item.position, title: item.title || "", url: item.url || "", image: item.image ? [item.image] : [] } : empty);
    setDlg(item || "new");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image.length) return toast.error("Gambar wajib diisi");
    setBusy(true);
    const payload = { position: form.position, title: form.title, url: form.url, image: form.image[0] };
    try {
      if (dlg === "new") await api.post("/banners", payload);
      else await api.put(`/banners/${dlg.id}`, payload);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/banners/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="banner-tab">
      <p className="text-sm text-slate-500 mb-6">{t("banner.note")}</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {POSITIONS.map((pos) => {
          const b = items.find((x) => x.position === pos);
          return (
            <div key={pos} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-5" data-testid={`banner-slot-${pos}`}>
              <p className="text-sm font-semibold text-emerald-950 mb-3">{t(`banner.${pos}`)}</p>
              {b ? (
                <>
                  <div className={`overflow-hidden rounded-lg border border-slate-200 bg-emerald-50 ${pos === "top" ? "aspect-[8/1]" : "aspect-[1/3]"}`}>
                    <img src={imgUrl(b.image)} alt={b.title || pos} className="h-full w-full object-cover" />
                  </div>
                  {b.url && <p className="text-xs text-emerald-700 mt-2 truncate">{b.url}</p>}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => open(b)} data-testid={`banner-edit-${pos}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(b.id)} data-testid={`banner-delete-${pos}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
                  </div>
                </>
              ) : (
                <Button size="sm" onClick={() => { setForm({ ...empty, position: pos }); setDlg("new"); }} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid={`banner-add-${pos}`}>
                  <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("dash.banner")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="banner-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Posisi</Label>
              <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })} disabled={dlg !== "new"}>
                <SelectTrigger className="mt-1.5" data-testid="banner-form-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((p) => <SelectItem key={p} value={p} data-testid={`banner-pos-${p}`}>{t(`banner.${p}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Judul ({t("common.optional")})</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="banner-form-title" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Tautan tujuan ({t("common.optional")})</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" className="mt-1.5" data-testid="banner-form-url" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Gambar Banner</Label>
              <div className="mt-1.5"><ImagePicker values={form.image} onChange={(image) => setForm({ ...form, image })} max={1} testId="banner-image-picker" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="banner-form-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
